import React, { useState, useEffect } from 'react';
import './DashboardSchoolAnalytics.css';
import { supabase } from '../../lib/supabase';

const DashboardSchoolAnalytics = ({ dataLoading, setDataLoading, students, teachers, schoolData }) => {
  const [analyticsData, setAnalyticsData] = useState({
    studentsByGrade: {},
    teachersBySubject: {},
    recentActivity: [],
    totalLogins: 0,
    activeUsers: 0
  });
  const [analyticsLoading, setAnalyticsLoading] = useState(true);

  useEffect(() => {
    if (!dataLoading && students && teachers) {
      generateAnalytics();
    }
  }, [dataLoading, students, teachers]);

  const generateAnalytics = async () => {
    try {
      setAnalyticsLoading(true);
      
      // Process students by grade
      const studentsByGrade = {};
      students.forEach(student => {
        const grade = student.grade || 'No Grade';
        studentsByGrade[grade] = (studentsByGrade[grade] || 0) + 1;
      });

      // Process teachers by subject
      const teachersBySubject = {};
      teachers.forEach(teacher => {
        const subject = teacher.subject || 'No Subject';
        teachersBySubject[subject] = (teachersBySubject[subject] || 0) + 1;
      });

      // Fetch recent activity
      const { data: activities } = await supabase
        .from('cms_activities')
        .select('*')
        .order('added_at', { ascending: false })
        .limit(20);

      // Calculate active users (users who logged in recently)
      const activeUsers = students.filter(s => s.is_active).length + teachers.filter(t => t.is_active).length;

      setAnalyticsData({
        studentsByGrade,
        teachersBySubject,
        recentActivity: activities || [],
        totalLogins: activities?.length || 0,
        activeUsers
      });

    } catch (error) {
      } finally {
      setAnalyticsLoading(false);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const activityTime = new Date(timestamp);
    const diffInSeconds = Math.floor((now - activityTime) / 1000);

    if (diffInSeconds < 60) {
      return 'Just now';
    } else if (diffInSeconds < 3600) {
      const minutes = Math.floor(diffInSeconds / 60);
      return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
    } else if (diffInSeconds < 86400) {
      const hours = Math.floor(diffInSeconds / 3600);
      return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    } else {
      const days = Math.floor(diffInSeconds / 86400);
      return `${days} day${days > 1 ? 's' : ''} ago`;
    }
  };

  const getActivityIcon = (title) => {
    if (title.toLowerCase().includes('student')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
        </svg>
      );
    } else if (title.toLowerCase().includes('teacher')) {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z"/>
        </svg>
      );
    } else {
      return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
          <path d="M19 13H13V19H11V13H5V11H11V5H13V11H19V13Z"/>
        </svg>
      );
    }
  };

  if (analyticsLoading) {
    return (
      <div className="school-analytics-loading">
        <div className="school-analytics-spinner">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L13.09 8.26L22 9L13.09 9.74L12 16L10.91 9.74L2 9L10.91 8.26L12 2Z"/>
          </svg>
        </div>
        <p>Generating analytics...</p>
      </div>
    );
  }

  return (
    <div className="school-analytics-content">
      <div className="school-analytics-header">
        <h2>School Analytics</h2>
        <p>Insights and statistics for {schoolData?.name || 'your school'}</p>
      </div>

      {/* Key Metrics */}
      <div className="school-analytics-metrics">
        <div className="school-analytics-metric-card">
          <div className="school-analytics-metric-icon school-analytics-students-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"/>
            </svg>
          </div>
          <div className="school-analytics-metric-content">
            <h3>Total Students</h3>
            <div className="school-analytics-metric-number">{students.length}</div>
            <div className="school-analytics-metric-label">Active Students</div>
          </div>
        </div>

        <div className="school-analytics-metric-card">
          <div className="school-analytics-metric-icon school-analytics-teachers-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9V7L15 1H5C3.89 1 3 1.89 3 3V21C3 22.11 3.89 23 5 23H19C20.11 23 21 22.11 21 21V9M19 9H14V4H19V9Z"/>
            </svg>
          </div>
          <div className="school-analytics-metric-content">
            <h3>Total Teachers</h3>
            <div className="school-analytics-metric-number">{teachers.length}</div>
            <div className="school-analytics-metric-label">Active Teachers</div>
          </div>
        </div>

        <div className="school-analytics-metric-card">
          <div className="school-analytics-metric-icon school-analytics-activity-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 3H5C3.9 3 3 3.9 3 5V19C3 20.1 3.9 21 5 21H19C20.1 21 21 20.1 21 19V5C21 3.9 20.1 3 19 3ZM19 19H5V5H19V19ZM17 12H7V10H17V12ZM15 16H7V14H15V16ZM17 8H7V6H17V8Z"/>
            </svg>
          </div>
          <div className="school-analytics-metric-content">
            <h3>Active Users</h3>
            <div className="school-analytics-metric-number">{analyticsData.activeUsers}</div>
            <div className="school-analytics-metric-label">Currently Active</div>
          </div>
        </div>

        <div className="school-analytics-metric-card">
          <div className="school-analytics-metric-icon school-analytics-logins-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
            </svg>
          </div>
          <div className="school-analytics-metric-content">
            <h3>Recent Activities</h3>
            <div className="school-analytics-metric-number">{analyticsData.totalLogins}</div>
            <div className="school-analytics-metric-label">Last 20 Activities</div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="school-analytics-charts">
        <div className="school-analytics-chart-container">
          <div className="school-analytics-chart-header">
            <h3>Students by Grade</h3>
            <p>Distribution of students across different grades</p>
          </div>
          <div className="school-analytics-chart-content">
            {Object.keys(analyticsData.studentsByGrade).length === 0 ? (
              <div className="school-analytics-no-data">
                <div className="school-analytics-no-data-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <p>No grade data available</p>
              </div>
            ) : (
              <div className="school-analytics-grade-chart">
                {Object.entries(analyticsData.studentsByGrade).map(([grade, count]) => (
                  <div key={grade} className="school-analytics-grade-item">
                    <div className="school-analytics-grade-bar">
                      <div 
                        className="school-analytics-grade-fill"
                        style={{ 
                          width: `${(count / Math.max(...Object.values(analyticsData.studentsByGrade))) * 100}%` 
                        }}
                      ></div>
                    </div>
                    <div className="school-analytics-grade-info">
                      <span className="school-analytics-grade-name">{grade}</span>
                      <span className="school-analytics-grade-count">{count} students</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="school-analytics-chart-container">
          <div className="school-analytics-chart-header">
            <h3>Teachers by Subject</h3>
            <p>Distribution of teachers across different subjects</p>
          </div>
          <div className="school-analytics-chart-content">
            {Object.keys(analyticsData.teachersBySubject).length === 0 ? (
              <div className="school-analytics-no-data">
                <div className="school-analytics-no-data-icon">
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                  </svg>
                </div>
                <p>No subject data available</p>
              </div>
            ) : (
              <div className="school-analytics-subject-chart">
                {Object.entries(analyticsData.teachersBySubject).map(([subject, count]) => (
                  <div key={subject} className="school-analytics-subject-item">
                    <div className="school-analytics-subject-bar">
                      <div 
                        className="school-analytics-subject-fill"
                        style={{ 
                          width: `${(count / Math.max(...Object.values(analyticsData.teachersBySubject))) * 100}%` 
                        }}
                      ></div>
                    </div>
                    <div className="school-analytics-subject-info">
                      <span className="school-analytics-subject-name">{subject}</span>
                      <span className="school-analytics-subject-count">{count} teachers</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      {/* <div className="school-analytics-activity">
        <div className="school-analytics-activity-header">
          <h3>Recent Activity</h3>
          <p>Latest activities in your school</p>
        </div>
        <div className="school-analytics-activity-content">
          {analyticsData.recentActivity.length === 0 ? (
            <div className="school-analytics-no-data">
              <div className="school-analytics-no-data-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
              </div>
              <p>No recent activity</p>
            </div>
          ) : (
            <div className="school-analytics-activity-list">
              {analyticsData.recentActivity.slice(0, 10).map((activity) => (
                <div key={activity.id} className="school-analytics-activity-item">
                  <div className="school-analytics-activity-icon">
                    {getActivityIcon(activity.title)}
                  </div>
                  <div className="school-analytics-activity-content">
                    <div className="school-analytics-activity-title">{activity.title}</div>
                    {activity.details && (
                      <div className="school-analytics-activity-details">{activity.details}</div>
                    )}
                    <div className="school-analytics-activity-time">{formatTimeAgo(activity.added_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div> */}
    </div>
  );
};

export default DashboardSchoolAnalytics;
